<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import { MENU_GROUPS, canAccessPage, pageByPath } from '@/data/registry'
import { useAuthStore } from '@/stores/auth'
import { usePrototypeStore } from '@/stores/prototype'
import { useUiStore } from '@/stores/ui'
import { useDataStore } from '@/stores/data'
import { useTaskStore } from '@/stores/tasks'
import { adapter } from '@/services/prototype-adapter'
import { useWorkspaceStore } from '@/stores/workspace'
import { useSessionCacheStore } from '@/stores/session-cache'
import { formatDateTime } from '@/utils/format'
import AppIcon from '@/components/AppIcon.vue'
import ProSelect from '@/components/pro/ProSelect.vue'
import GlobalSearch from '@/components/shell/GlobalSearch.vue'
import TaskCenter from '@/components/shell/TaskCenter.vue'
import HelpCenter from '@/components/shell/HelpCenter.vue'
import NotificationPanel from '@/components/shell/NotificationPanel.vue'
import PrototypeDevPanel from '@/components/shell/PrototypeDevPanel.vue'
import RolePreviewDialog from '@/components/shell/RolePreviewDialog.vue'

const route = useRoute()
const router = useRouter()
const auth = useAuthStore()
const prototype = usePrototypeStore()
const taskStore = useTaskStore()
const sessionCache = useSessionCacheStore()
const ui = useUiStore()
const data = useDataStore()
const workspace = useWorkspaceStore()

const navScroll = ref<HTMLElement | null>(null)
const tabsScroll = ref<HTMLElement | null>(null)
const openGroups = ref<Record<string, boolean>>({})
const mobileOpen = computed(() => ui.mobileNavOpen)
const currentPage = computed(() => route.meta.title ?? '运营工作台')
const alertCount = computed(() => prototype.badges.openAlerts)
const taskCount = computed(() => taskStore.running.length)
const regions = computed(() => [{ value: 'all', label: '全部区域' }, ...adapter.options('regions')])
const dateLabel = '2026年10月17日 星期六'
const periodLabel = '2026/10/11 - 2026/10/17'
const scopeValue = computed(() => ui.dataScopeId || 'all')
const scopeLabel = computed(() => regions.value.find(item => item.value === scopeValue.value)?.label ?? '全部区域')

const NAV_ICONS: Record<string, string> = {
  仪表盘: 'dashboard',
  用户管理: 'users',
  家庭档案: 'family',
  用药管理: 'pill',
  健康监测: 'heart',
  药品库: 'drug',
  消息通知: 'bell',
  运营中心: 'operations',
  客服与反馈: 'support',
  数据分析: 'analytics',
  系统设置: 'settings',
}

const NAV_BADGES: Record<string, string> = {
  'medication-plans': 'pendingReminders',
  'health-alerts': 'openAlerts',
  devices: 'offlineDevices',
  'drug-review': 'pendingReviews',
  'support-tickets': 'openTickets',
  'support-feedback': 'pendingFeedback',
}

/** Menu, breadcrumb, route guard and tabs all consume the generated page registry. */
const visibleGroups = computed(() => MENU_GROUPS.map(group => ({
  key: group.title,
  label: group.title,
  icon: NAV_ICONS[group.title] ?? group.items[0]?.icon ?? 'grid',
  direct: group.items.length === 1,
  items: group.items
    .filter(item => auth.has(item.permission))
    .map(item => ({
      id: item.id,
      title: item.title,
      route: item.route,
      permission: item.permission,
      badge: NAV_BADGES[item.id],
    })),
})).filter(group => group.items.length > 0))

const crumb = computed(() => {
  const pageGroup = String(route.meta.group ?? '')
  const title = String(route.meta.title ?? '页面')
  return {
    group: pageGroup || '运营工作台',
    title,
    showGroup: Boolean(pageGroup && pageGroup !== title),
  }
})

function badgeCount(name?: string): number {
  if (!name) return 0
  return Number((prototype.badges as Record<string, number>)[name] ?? 0)
}

function toggleGroup(key: string): void {
  openGroups.value[key] = !isGroupOpen(key)
}

function isGroupOpen(key: string): boolean {
  if (openGroups.value[key] !== undefined) return openGroups.value[key]!
  const group = visibleGroups.value.find(item => item.key === key)
  return Boolean(group?.items.some(item => routePath(item.route) === route.path)) || key === 'dashboard'
}

function href(path: string): string {
  return routePath(path)
}

function routePath(path: string): string {
  return path.replace(/^#/, '').split('?')[0] || '/'
}

function navigate(path: string): void {
  ui.mobileNavOpen = false
  void router.push(path)
}

function scrollActiveIntoView(container: HTMLElement | null, selector: string): void {
  const active = container?.querySelector(selector) as HTMLElement | null
  if (!active || !container) return
  const box = container.getBoundingClientRect()
  const item = active.getBoundingClientRect()
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches
  if (item.top < box.top + 8 || item.bottom > box.bottom - 8) {
    container.scrollTo({ top: container.scrollTop + (item.top - box.top) - box.height / 2 + item.height / 2, behavior: reduced ? 'auto' : 'smooth' })
  }
}

async function syncSidebar(): Promise<void> {
  await nextTick()
  scrollActiveIntoView(navScroll.value, '.nav-link--active')
  if (tabsScroll.value) {
    const active = tabsScroll.value.querySelector('.workspace-tab--active') as HTMLElement | null
    if (active) {
      const box = tabsScroll.value.getBoundingClientRect()
      const item = active.getBoundingClientRect()
      if (item.left < box.left || item.right > box.right) {
        active.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' })
      }
    }
  }
}

watch(() => auth.effectiveRole, () => {
  const page = pageByPath(route.path)
  if (page && !canAccessPage(page, auth.has)) {
    void router.replace({ path: '/403', query: { from: route.fullPath, need: page.permission } })
  }
})

watch(() => route.path, () => { void syncSidebar() })
watch(mobileOpen, () => { void syncSidebar() })

function closeTab(path: string, event: Event): void {
  event.stopPropagation()
  const next = workspace.close(path)
  if (next) void router.push(next)
}

function switchTab(path: string): void {
  if (path !== route.path) void router.push(path)
}

function restoreTab(): void {
  const next = workspace.reopen()
  if (next) void router.push(next.fullPath)
  else ui.toast('info', '没有可恢复的页面标签')
}

function dragStart(event: DragEvent, index: number): void {
  event.dataTransfer?.setData('text/plain', String(index))
  if (event.dataTransfer) event.dataTransfer.effectAllowed = 'move'
}

function dropTab(event: DragEvent, index: number): void {
  const from = Number(event.dataTransfer?.getData('text/plain'))
  if (Number.isFinite(from)) workspace.reorder(from, index)
}

function handleMenuCommand(command: string): void {
  if (command === 'profile') void router.push('/profile')
  if (command === 'settings') void router.push('/settings/general')
  if (command === 'help') ui.helpOpen = true
  if (command === 'role') ui.rolePreviewOpen = true
  if (command === 'reset') void resetData()
  if (command === 'logout') void logout()
}

async function resetData(): Promise<void> {
  const allowed = await ui.confirm({
    title: '重置原型数据',
    message: '将清空当前浏览器内存中的新增、编辑和状态变化，并恢复可复现种子数据。',
    detail: '此操作不会影响任何数据库或正式服务。',
    confirmText: '重置数据',
    tone: 'danger',
  })
  if (!allowed) return
  data.reset()
  taskStore.tasks.splice(0)
  taskStore.addSeedTasks(prototype.db.async_tasks as Array<Record<string, unknown>>)
  workspace.clearAllStored()
  sessionCache.clear()
  ui.toast('success', '原型数据已恢复', '刷新后仍会重新生成同一份种子数据。')
  void router.push('/dashboard')
}

async function logout(): Promise<void> {
  const allowed = await ui.confirm({
    title: '退出当前会话',
    message: '退出后需要重新输入管理员账号与口令。',
    confirmText: '退出登录',
  })
  if (!allowed) return
  auth.logout('管理员主动退出')
  workspace.resetTabs()
  void router.push('/login')
}

function onKeydown(event: KeyboardEvent): void {
  if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
    event.preventDefault()
    ui.commandOpen = true
  }
  if (event.key === 'Escape') {
    ui.commandOpen = false
    ui.mobileNavOpen = false
  }
}

function syncOnline(): void {
  ui.setOnline(navigator.onLine)
}

onMounted(() => {
  window.addEventListener('keydown', onKeydown)
  window.addEventListener('online', syncOnline)
  window.addEventListener('offline', syncOnline)
  data.startTicker()
  void syncSidebar()
})

onBeforeUnmount(() => {
  window.removeEventListener('keydown', onKeydown)
  window.removeEventListener('online', syncOnline)
  window.removeEventListener('offline', syncOnline)
  data.stopTicker()
})

function updateScope(value: string | number | boolean | string[]): void {
  ui.dataScopeId = String(value ?? 'all')
  ElMessage.success(`数据范围已切换为${regions.value.find(item => String(item.value) === String(value))?.label ?? '全部区域'}`)
}
</script>

<template>
  <div class="app-root">
    <a href="#main-content" class="skip-link">跳到主内容</a>
    <div v-if="ui.routeSwitching" class="route-progress" role="progressbar" aria-label="页面切换中"><i /></div>
    <div class="app-shell">
      <div class="sidebar-scrim" :class="{ 'is-open': mobileOpen }" @click="ui.mobileNavOpen = false" />
      <aside class="app-sidebar" :class="{ 'is-open': mobileOpen }" aria-label="主导航">
        <RouterLink to="/dashboard" class="brand" aria-label="康护健康运营工作台">
          <span class="brand__mark">
            <svg viewBox="0 0 48 48" width="22" height="22" fill="none" aria-hidden="true"><path d="M10 25h7l3-7 5 15 4-9h9" stroke="#fff" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round" /></svg>
          </span>
          <span class="brand__text">
            <span class="brand__name">康护健康</span>
            <span class="brand__descriptor">运营工作台</span>
          </span>
        </RouterLink>
        <nav ref="navScroll" class="sidebar-nav" aria-label="业务菜单">
          <div v-for="group in visibleGroups" :key="group.key" class="nav-group">
            <template v-if="group.direct && group.items.length === 1">
              <RouterLink
                v-for="item in group.items"
                :key="item.id"
                :to="href(item.route)"
                class="nav-link"
                :class="{ 'nav-link--active': route.path === routePath(item.route) }"
                :aria-current="route.path === routePath(item.route) ? 'page' : undefined"
                @click="ui.mobileNavOpen = false"
              >
                <span class="nav-link__icon"><AppIcon :name="item.id === 'analytics' ? 'analytics' : group.key === 'families' ? 'family' : group.icon" :size="17" /></span>
                <span class="nav-link__label">{{ item.title }}</span>
                <span v-if="item.badge && badgeCount(item.badge)" class="nav-badge">{{ badgeCount(item.badge) > 99 ? '99+' : badgeCount(item.badge) }}</span>
              </RouterLink>
            </template>
            <template v-else>
              <button type="button" class="nav-link nav-group__toggle" :aria-expanded="isGroupOpen(group.key)" @click="toggleGroup(group.key)">
                <span class="nav-link__icon"><AppIcon :name="group.icon" :size="17" /></span>
                <span class="nav-link__label">{{ group.label }}</span>
                <AppIcon name="chevron-right" :size="13" class="nav-chevron" :class="{ 'nav-chevron--open': isGroupOpen(group.key) }" />
              </button>
              <div v-show="isGroupOpen(group.key)" class="nav-sub">
                <RouterLink
                  v-for="item in group.items"
                  :key="item.id"
                  :to="href(item.route)"
                  class="nav-link"
                  :class="{ 'nav-link--active': route.path === routePath(item.route) }"
                  :aria-current="route.path === routePath(item.route) ? 'page' : undefined"
                  @click="ui.mobileNavOpen = false"
                >
                  <span class="nav-link__label">{{ item.title }}</span>
                  <span v-if="item.badge && badgeCount(item.badge)" class="nav-badge">{{ badgeCount(item.badge) > 99 ? '99+' : badgeCount(item.badge) }}</span>
                </RouterLink>
              </div>
            </template>
          </div>
        </nav>
        <div class="sidebar-footer">
          <RouterLink to="/health/monitoring" class="guardian-card" aria-label="查看家庭健康守护数据">
            <span class="guardian-card__art" aria-hidden="true">
              <svg viewBox="0 0 120 48" width="120" height="48" fill="none">
                <path d="M4 44c10-15 24-18 36-10 9 6 17 6 27-2 12-9 30-7 49 12" stroke="#9fdcc6" stroke-width="1.5" opacity=".8"/>
                <path d="M28 42V26l14-9 14 9v16" fill="#fff" stroke="#77c6aa" stroke-width="1.4"/>
                <path d="M38 42V33h8v9" fill="#d9f3e8" stroke="#77c6aa" stroke-width="1.2"/>
                <circle cx="86" cy="22" r="9" fill="#fff" stroke="#77c6aa" stroke-width="1.3"/>
                <path d="M82 22h8M86 18v8" stroke="#08b578" stroke-width="1.6" stroke-linecap="round"/>
              </svg>
            </span>
            <span class="guardian-card__title">用科技守护<br />每一个家庭的健康</span>
            <span class="guardian-card__count">今日待跟进 {{ prototype.badges.openAlerts }} 条健康预警</span>
          </RouterLink>
          <div class="sidebar-foot-links">
            <button type="button" @click="ui.helpOpen = true">帮助中心</button>
            <button type="button" @click="ui.commandOpen = true">全局搜索</button>
          </div>
        </div>
      </aside>

      <div class="app-main">
        <header class="topbar">
          <button type="button" class="topbar__menu" aria-label="打开菜单" @click="ui.mobileNavOpen = !ui.mobileNavOpen">
            <AppIcon name="menu" :size="20" />
          </button>
          <button type="button" class="global-search" @click="ui.commandOpen = true">
            <AppIcon name="search" :size="16" />
            <span>搜索页面、用户、订单或工单</span>
            <span class="global-search__shortcut">⌘ K</span>
          </button>
          <RouterLink to="/analytics" class="topbar__dates" aria-label="当前统计周期，前往经营分析切换">
            <AppIcon name="calendar" :size="15" />
            <span>{{ periodLabel }}</span>
            <AppIcon name="chevron-down" :size="12" />
          </RouterLink>
          <label class="topbar__period">
            <AppIcon name="compass" :size="15" />
            <span class="visually-hidden">数据范围</span>
            <ProSelect
              :model-value="scopeValue"
              :options="regions"
              :filterable="false"
              :clearable="false"
              aria-label="数据范围"
              @update:model-value="updateScope"
            />
          </label>
          <div class="topbar__actions">
            <button v-if="auth.has('tasks.view')" type="button" class="icon-button icon-button--optional" aria-label="任务中心" title="任务中心" @click="ui.tasksOpen = true">
              <AppIcon name="task" :size="18" />
              <span v-if="taskCount" class="icon-button__dot">{{ taskCount }}</span>
            </button>
            <button type="button" class="icon-button icon-button--optional" aria-label="帮助中心" title="帮助中心" @click="ui.helpOpen = true">
              <AppIcon name="help" :size="18" />
            </button>
            <button type="button" class="icon-button" aria-label="通知中心" title="通知中心" @click="ui.notificationsOpen = true">
              <AppIcon name="bell" :size="18" />
              <span v-if="data.unreadCount" class="icon-button__dot">{{ data.unreadCount > 99 ? '99+' : data.unreadCount }}</span>
            </button>
            <el-dropdown trigger="click" @command="handleMenuCommand">
              <button type="button" class="topbar__user" aria-label="账号菜单">
                <span class="avatar">{{ auth.profile?.name?.slice(-2) || '管理员' }}</span>
                <span class="topbar__user-meta">
                  <span class="topbar__user-name">{{ auth.profile?.name || '未登录' }}</span>
                  <span class="topbar__user-role">{{ auth.roleName }}<template v-if="auth.isViewingAs"> · 视角预览</template></span>
                </span>
                <AppIcon name="chevron-down" :size="13" />
              </button>
              <template #dropdown>
                <el-dropdown-menu>
                  <el-dropdown-item command="profile">个人中心</el-dropdown-item>
                  <el-dropdown-item command="settings">系统参数</el-dropdown-item>
                  <el-dropdown-item command="help">帮助中心</el-dropdown-item>
                  <el-dropdown-item v-if="auth.canPreviewRoles" command="role" divided>角色视角预览</el-dropdown-item>
                  <el-dropdown-item command="reset" divided>重置原型数据</el-dropdown-item>
                  <el-dropdown-item command="logout" divided>退出登录</el-dropdown-item>
                </el-dropdown-menu>
              </template>
            </el-dropdown>
          </div>
        </header>

        <div v-if="auth.isViewingAs" class="role-preview-banner" role="status">
          <AppIcon name="eye" :size="15" />
          <span>正在以“{{ auth.roleName }}”视角预览菜单与权限，实际登录身份仍为{{ auth.profile?.name }}。</span>
          <button type="button" class="text-button" @click="auth.switchViewAs(null)">退出预览</button>
        </div>

        <div class="workspace">
          <div class="tabsbar">
            <div ref="tabsScroll" class="tabsbar__scroll" role="tablist" aria-label="已打开页面">
              <button
                v-for="(tab, index) in workspace.tabs"
                :key="tab.key"
                type="button"
                class="workspace-tab"
                :class="{ 'workspace-tab--active': route.path === tab.path, 'workspace-tab--pinned': tab.pinned }"
                role="tab"
                :aria-selected="route.path === tab.path"
                draggable="true"
                @click="switchTab(tab.path)"
                @dragstart="dragStart($event, index)"
                @dragover.prevent
                @drop="dropTab($event, index)"
              >
                <span class="workspace-tab__title">{{ tab.title }}</span>
                <span v-if="route.path === tab.path" class="workspace-tab__live" aria-hidden="true" />
                <span v-if="!tab.pinned" class="workspace-tab__close" role="button" tabindex="0" aria-label="关闭标签" @click="closeTab(tab.path, $event)" @keydown.enter="closeTab(tab.path, $event)">×</span>
              </button>
            </div>
            <div class="tabsbar__tools">
              <button type="button" class="tabsbar__tool" aria-label="恢复关闭的标签" title="恢复关闭的标签" @click="restoreTab"><AppIcon name="history" :size="14" /></button>
              <el-dropdown trigger="click" @command="(command: string | number | object) => { if (command === 'others') workspace.closeOthers(route.path); else if (command === 'all') { void router.push(workspace.closeAll()) } else { switchTab(String(command)) } }">
                <button type="button" class="tabsbar__tool" aria-label="标签操作" title="标签操作"><AppIcon name="more-horizontal" :size="15" /></button>
                <template #dropdown>
                  <el-dropdown-menu>
                    <el-dropdown-item command="others">关闭其他标签</el-dropdown-item>
                    <el-dropdown-item command="all">关闭全部标签</el-dropdown-item>
                    <el-dropdown-item divided disabled>已打开标签（{{ workspace.tabs.length }}）</el-dropdown-item>
                    <el-dropdown-item v-for="tab in workspace.tabs" :key="tab.key" :command="tab.fullPath">
                      <span :class="{ 'success-text': route.path === tab.path }">{{ tab.title }}</span>
                    </el-dropdown-item>
                  </el-dropdown-menu>
                </template>
              </el-dropdown>
            </div>
          </div>
          <div class="breadcrumb" aria-label="面包屑">
            <span>运营工作台</span>
            <span class="breadcrumb__sep">/</span>
            <template v-if="crumb.showGroup">
              <span>{{ crumb.group }}</span>
              <span class="breadcrumb__sep">/</span>
            </template>
            <span class="breadcrumb__current" aria-current="page">{{ crumb.title }}</span>
            <span class="breadcrumb__right">{{ dateLabel }} · {{ scopeLabel }}</span>
          </div>
        </div>

        <main id="main-content" class="content" tabindex="-1">
          <div v-if="!ui.online" class="offline-inline" role="status">
            <AppIcon name="wifi-off" :size="15" />
            <span>网络已断开，当前页面和筛选条件已保留。</span>
            <RouterLink to="/offline">查看恢复说明</RouterLink>
          </div>
          <slot />
        </main>
      </div>
    </div>

    <GlobalSearch :open="ui.commandOpen" @close="ui.commandOpen = false" />
    <TaskCenter :open="ui.tasksOpen" @close="ui.tasksOpen = false" />
    <HelpCenter :open="ui.helpOpen" @close="ui.helpOpen = false" />
    <NotificationPanel :open="ui.notificationsOpen" @close="ui.notificationsOpen = false" />
    <PrototypeDevPanel v-if="auth.primaryRole === 'super_admin' && prototype.settings.prototypeDevMode" />
    <RolePreviewDialog />
  </div>
</template>
